import React, { useState, useEffect, useRef } from 'react';

function MultiMonthSelector({ selectedMonths, onMonthsChange, year, availableMonths, onConfirm }) {
  const [isOpen, setIsOpen] = useState(false);
  const [tempSelection, setTempSelection] = useState(selectedMonths);
  const dropdownRef = useRef(null);
  
  // Use provided months or default to all months for current year
  const defaultMonths = [
    { value: '01', label: 'January' },
    { value: '02', label: 'February' },
    { value: '03', label: 'March' },
    { value: '04', label: 'April' },
    { value: '05', label: 'May' },
    { value: '06', label: 'June' },
    { value: '07', label: 'July' },
    { value: '08', label: 'August' },
    { value: '09', label: 'September' },
    { value: '10', label: 'October' },
    { value: '11', label: 'November' },
    { value: '12', label: 'December' }
  ];

  // Use availableMonths only to determine distinct years for grouping.
  // Always show all 12 months so the user can filter by any month.
  const distinctYears = [...new Set((availableMonths || []).map(m => m.year).filter(Boolean))].sort();
  const hasMultipleYears = distinctYears.length > 1;

  const months = hasMultipleYears
    ? distinctYears.flatMap(y => defaultMonths.map(m => ({ ...m, year: y, value: `${y}-${m.value}` })))
    : defaultMonths;

  // Sync temp selection with props. Preserve full selection across remounts
  // (tab switches) even when the months list hasn't loaded yet — filtering
  // here wipes the checkmarks if `availableMonths` arrives late.
  useEffect(() => {
    setTempSelection(selectedMonths);
  }, [selectedMonths]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        // Reset temp selection if closed without confirming
        setTempSelection(selectedMonths);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [selectedMonths]);

  // Compare month values tolerant of the two formats in use ("MM" and
  // "YYYY-MM"). A user's saved selection from a previous session may arrive
  // in a different format than what the current `months` list uses.
  const isSameMonthValue = (a, b) => {
    if (a === b) return true;
    return String(a).slice(-2) === String(b).slice(-2);
  };

  const isMonthSelected = (monthValue) => tempSelection.some(v => isSameMonthValue(v, monthValue));

  const handleToggleMonth = (monthValue) => {
    const newSelection = isMonthSelected(monthValue)
      ? tempSelection.filter(m => !isSameMonthValue(m, monthValue))
      : [...tempSelection, monthValue];

    setTempSelection(newSelection);
  };

  const handleSelectAll = () => {
    setTempSelection(months.map(m => m.value));
  };

  const handleClearAll = () => {
    setTempSelection([]);
  };

  const handleSelectYear = (selectedYear) => {
    // Select all months for a specific year
    const yearMonths = months
      .filter(m => !m.year || m.year === selectedYear)
      .map(m => m.value);
    
    // Toggle year selection
    const allYearMonthsSelected = yearMonths.every(month => isMonthSelected(month));

    if (allYearMonthsSelected) {
      // Deselect all months from this year
      setTempSelection(tempSelection.filter(m => !yearMonths.some(ym => isSameMonthValue(ym, m))));
    } else {
      // Select all months from this year (drop any previous entries that match the same month in either format)
      const filtered = tempSelection.filter(m => !yearMonths.some(ym => isSameMonthValue(ym, m)));
      setTempSelection([...filtered, ...yearMonths]);
    }
  };

  const handleConfirm = () => {
    onMonthsChange(tempSelection);
    if (onConfirm) {
      onConfirm(tempSelection);
    }
    setIsOpen(false);
  };

  const handleCancel = () => {
    setTempSelection(selectedMonths);
    setIsOpen(false);
  };

  const getSelectedLabel = () => {
    if (selectedMonths.length === 0) {
      return 'Select months';
    } else if (selectedMonths.length === 1) {
      const month = months.find(m => m.value === selectedMonths[0]);
      return month ? month.label : 'Select months';
    } else if (selectedMonths.length === months.length) {
      return 'All available months';
    } else {
      return `${selectedMonths.length} months selected`;
    }
  };

  // Group months by year
  const groupedMonths = {};
  months.forEach(month => {
    const monthYear = month.year || year;
    if (!groupedMonths[monthYear]) {
      groupedMonths[monthYear] = [];
    }
    groupedMonths[monthYear].push(month);
  });

  return (
    <div className="multi-month-selector" ref={dropdownRef}>
      <button
        className="month-selector-button"
        onClick={() => setIsOpen(!isOpen)}
        type="button"
      >
        <span className="selector-label">{getSelectedLabel()}</span>
        {!hasMultipleYears && <span className="selector-year">{year}</span>}
        <span className={`selector-arrow ${isOpen ? 'open' : ''}`}>▼</span>
      </button>
      
      {isOpen && (
        <div className="month-dropdown">
          <div className="dropdown-header">
            <button
              className="dropdown-action"
              onClick={handleSelectAll}
              type="button"
            >
              Select All
            </button>
            <button
              className="dropdown-action"
              onClick={handleClearAll}
              type="button"
            >
              Clear All
            </button>
          </div>
          
          <div className="month-list">
            {hasMultipleYears ? (
              // Show grouped by year
              Object.keys(groupedMonths).sort().map(groupYear => {
                const yearMonths = groupedMonths[groupYear];
                const yearMonthValues = yearMonths.map(m => m.value);
                const allYearSelected = yearMonthValues.every(v => isMonthSelected(v));
                const someYearSelected = yearMonthValues.some(v => isMonthSelected(v));
                
                return (
                  <div key={groupYear} className="year-group">
                    <div className="year-header">
                      <label className="year-option">
                        <input
                          type="checkbox"
                          checked={allYearSelected}
                          indeterminate={someYearSelected && !allYearSelected}
                          onChange={() => handleSelectYear(groupYear)}
                          className="year-checkbox"
                        />
                        <span className="year-label">{groupYear}</span>
                      </label>
                    </div>
                    <div className="year-months">
                      {yearMonths.map(month => (
                        <label key={`${groupYear}-${month.value}`} className="month-option indented">
                          <input
                            type="checkbox"
                            checked={isMonthSelected(month.value)}
                            onChange={() => handleToggleMonth(month.value)}
                            className="month-checkbox"
                          />
                          <span className="month-label">{month.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                );
              })
            ) : (
              // Show flat list for single year
              months.map(month => (
                <label key={month.value} className="month-option">
                  <input
                    type="checkbox"
                    checked={isMonthSelected(month.value)}
                    onChange={() => handleToggleMonth(month.value)}
                    className="month-checkbox"
                  />
                  <span className="month-label">{month.label}</span>
                </label>
              ))
            )}
          </div>
          
          <div className="dropdown-footer">
            <div className="selection-info">
              <span className="selection-count">
                {tempSelection.length} of {months.length} selected
              </span>
            </div>
            <div className="dropdown-actions">
              <button
                className="btn btn-secondary btn-action"
                onClick={handleCancel}
                type="button"
              >
                Cancel
              </button>
              <button
                className="btn btn-primary btn-action"
                onClick={handleConfirm}
                type="button"
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default MultiMonthSelector;
